import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppConfiguration, CartLimitConfiguration } from '@models';
import { catchError, debounceTime, map, Observable, of, startWith, switchMap } from 'rxjs';
import { ApiService } from './api-service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);

  /** Re-emits on every token change, so each config below is re-read after sign-in. */
  private readonly tokenChanges$ = this.authService.tokenChanges$.pipe(startWith(null), debounceTime(10));

  /**
   * Event currency name from the backend ("Shopping Dollars"). Not gated on the player token: admin-panel has none
   * (MSAL authorizes its request), and in merch-store a pre-sign-in 401 just yields '' until the re-read after sign-in.
   * Empty while loading and on error — prices then show without a unit rather than a hardcoded one.
   */
  readonly rewardName = toSignal(this.tokenChanges$.pipe(switchMap(() => this.getRewardName())), {
    initialValue: '',
  });

  /**
   * RCP-468 — per-SKU quantity limits from the backend config. `null` until sign-in, while loading and on
   * error: the storefront then caps nothing itself and the backend's own check is the only one.
   */
  readonly cartLimits = toSignal(
    this.tokenChanges$.pipe(switchMap(() => (this.authService.token ? this.getCartLimits() : of(null)))),
    { initialValue: null },
  );

  // Never rethrow: an errored stream would kill the `rewardName` toSignal.
  private getRewardName(): Observable<string> {
    return this.apiService.get<AppConfiguration>('configuration').pipe(
      map((configuration) => configuration?.rewardName?.trim() ?? ''),
      catchError(() => of('')),
    );
  }

  // Never rethrow: an errored stream would kill the `cartLimits` toSignal.
  private getCartLimits(): Observable<CartLimitConfiguration | null> {
    return this.apiService.get<CartLimitConfiguration>('configuration/cart-limit').pipe(
      map((limits) => limits ?? null),
      catchError(() => of(null)),
    );
  }
}
